import { useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Eye, ExternalLink } from "lucide-react";
import { PdfPreview } from "@/components/PdfPreview";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

/**
 * Hook riutilizzabile per l'anteprima inline di allegati (PDF + immagini).
 *
 * Usage:
 *   const { openPreview, PreviewDialog } = useAttachmentPreview();
 *   // In un bottone:
 *   <button onClick={() => openPreview("/api/policies/123/attachments/456/download", { filename: "polizza.pdf", content_type: "application/pdf" })} />
 *   // In render:
 *   {PreviewDialog}
 */
export function useAttachmentPreview() {
  const [preview, setPreview] = useState(null); // { url, meta, mime }

  const openPreview = useCallback(async (downloadUrl, meta = {}) => {
    try {
      const response = await api.get(downloadUrl, { responseType: "blob" });
      const mime = meta.content_type || response.headers?.["content-type"] || "application/pdf";
      const typed = new Blob([response.data], { type: mime });
      const url = window.URL.createObjectURL(typed);
      setPreview({ url, meta, mime });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  const closePreview = useCallback(() => {
    setPreview((p) => {
      if (p?.url) {
        try { window.URL.revokeObjectURL(p.url); } catch { /* ignore */ }
      }
      return null;
    });
  }, []);

  const isImage = (preview?.mime || preview?.meta?.content_type || "").startsWith("image/");

  const PreviewDialog = (
    <Dialog open={!!preview} onOpenChange={(o) => { if (!o) closePreview(); }}>
      <DialogContent className="w-[95vw] max-w-5xl h-[90vh] max-h-[90vh] flex flex-col p-0" data-testid="attachment-preview-dialog">
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-slate-100">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Eye className="h-5 w-5 text-emerald-700" />
            <span className="truncate">{preview?.meta?.filename || "Anteprima allegato"}</span>
            {preview && (
              <a
                href={preview.url}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-auto text-xs text-blue-700 hover:text-blue-900 inline-flex items-center gap-1"
                data-testid="attachment-preview-open-tab"
                title="Apri in nuova scheda"
              >
                <ExternalLink className="h-3.5 w-3.5" /> Nuova scheda
              </a>
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-hidden bg-slate-100">
          {preview && (
            isImage ? (
              <div className="w-full h-full flex items-center justify-center p-4 overflow-auto">
                <img
                  src={preview.url}
                  alt={preview.meta.filename}
                  className="max-w-full max-h-full object-contain"
                  data-testid="attachment-preview-image"
                />
              </div>
            ) : (
              <PdfPreview fileUrl={preview.url} />
            )
          )}
        </div>
      </DialogContent>
    </Dialog>
  );

  return { openPreview, closePreview, PreviewDialog };
}

export default useAttachmentPreview;
